/**
 * Bagagem — "já está na mala?"
 *
 * Uma tela, três segmentos (Minha · Família · Viagem). O campo de adicionar
 * é fixo no rodapé e Enter adiciona sem sair; colar uma lista vira vários
 * itens; a categoria é opcional e "gruda" para os próximos. Marcar é tocar
 * a linha inteira; toque longo abre a folha de edição. Tudo offline.
 *
 * Família só aparece quando a pessoa está numa família nesta viagem —
 * famílias são opcionais e o segmento não fica cinza: some.
 */
import {
  BAGGAGE_CATEGORIES,
  BAGGAGE_CATEGORY_LABELS,
  type BaggageCategory,
  type BaggageScope,
  baggageProgress,
  featureEnabled,
  groupBaggage,
  splitPastedItems,
  tripPhase,
} from '@nv/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAgora } from '../../demo/DemoContext';
import { useOnline } from '../../sync/useOnline';
import { useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';
import { Card, EmptyState, ErrorState, Skeleton } from '../../ui/primitives';
import { type ItemDaMala, useBagagem } from './store';
import { Sugestoes } from './Sugestoes';
import { Sheet } from '../../ui/Sheet';

const ROTULO: Record<BaggageScope, string> = { private: 'Minha', family: 'Família', trip: 'Viagem' };

export function BagagemScreen() {
  const { trip, tripId, members, meuMemberId } = useTrip();
  const online = useOnline();
  const [params, setParams] = useSearchParams();
  const viagem = tripInputDaViagem(trip, members.length);
  const now = useAgora(viagem);
  const fase = viagem ? tripPhase(viagem, now) : 'antes';

  const { estado, carregando, erro, carregar, adicionar, marcar, editar, excluir, prepararVolta } = useBagagem();
  useEffect(() => {
    void carregar(tripId);
  }, [tripId, carregar]);

  const temFamilia = !!estado?.minhaFamiliaId;
  const escopos = ['private', ...(temFamilia ? ['family' as const] : []), 'trip'] as BaggageScope[];
  const escopoDaUrl = params.get('escopo') as BaggageScope | null;
  const [escopo, setEscopo] = useState<BaggageScope>(escopoDaUrl && ['private', 'family', 'trip'].includes(escopoDaUrl) ? escopoDaUrl : 'private');
  const [sugerindo, setSugerindo] = useState(false);
  useEffect(() => {
    if (escopo === 'family' && estado && !temFamilia) setEscopo('private');
  }, [escopo, estado, temFamilia]);

  const [texto, setTexto] = useState('');
  const [categoria, setCategoria] = useState<BaggageCategory>('outros');
  const [editando, setEditando] = useState<ItemDaMala | null>(null);
  const [soMeus, setSoMeus] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  // Novo item numa folha (PO, 18/09/2026): campo + categoria, sem rodapé fixo
  // comprimindo a tela. `/bagagem?novo=1` chega do "+" da barra já aberta.
  const [novoAberto, setNovoAberto] = useState(params.get('novo') === '1');
  useEffect(() => {
    if (novoAberto) window.setTimeout(() => entrada.current?.focus(), 50);
  }, [novoAberto]);

  const itens = useMemo(() => {
    const doEscopo = (estado?.items ?? []).filter((i) => i.scope === escopo);
    return escopo === 'trip' && soMeus ? doEscopo.filter((i) => i.assigneeMemberId === meuMemberId) : doEscopo;
  }, [estado, escopo, soMeus, meuMemberId]);
  const progresso = baggageProgress(itens);
  const grupos = groupBaggage(itens);
  const nomeDe = (memberId: string | null) => members.find((m) => m.memberId === memberId)?.name ?? null;

  if (!featureEnabled(trip.features, 'bagagem')) {
    return (
      <section className="screen bag">
        <EmptyState ilustracao="bagagem" title="Bagagem desligada" description="Quem administra a viagem desligou esta funcionalidade." />
      </section>
    );
  }

  async function enviarTexto() {
    const titulos = splitPastedItems(texto);
    if (titulos.length === 0) return;
    // Limpa ANTES de esperar: quem digita rápido já está no próximo item
    // quando o primeiro termina de guardar — limpar depois apagaria o que
    // acabou de ser escrito (o Enter seguinte não adicionava nada).
    setTexto('');
    entrada.current?.focus();
    await adicionar({ scope: escopo, titulos, category: categoria });
  }

  function trocar(novo: BaggageScope) {
    setEscopo(novo);
    setParams(novo === 'private' ? {} : { escopo: novo }, { replace: true });
  }

  return (
    <section className="screen bag">
      {/* Refino final: ações no alto; título 30/900 com "N de M" à direita. */}
      <header className="dia-header dia-header--dia bag__header">
        <div className="dia-header__titles bag__titulos">
          <h1 className="dia-header__title">Bagagem</h1>
          {progresso.total > 0 && (
            <span className="bag__contagem">
              {progresso.packed} de {progresso.total}
            </span>
          )}
        </div>
        {fase !== 'depois' && (
          <div className="bag__acoes dia-header__acoes">
            <button type="button" className="nv-btn nv-btn--chip bag__ideias" onClick={() => setSugerindo(true)} data-testid="bag-sugerir">
              <Icon name="destaque" size={16} />
              Ideias de mala
            </button>
            <button type="button" className="mem__mais bag__mais" onClick={() => setNovoAberto(true)} aria-label="Novo item" data-testid="bag-novo">
              <Icon name="criar" size={22} />
            </button>
          </div>
        )}
      </header>
      {sugerindo && <Sugestoes escopo={escopo} itens={(estado?.items ?? []).filter((i) => i.scope === escopo)} onFechar={() => setSugerindo(false)} />}

      {/* Mesmo desenho do Segmented (pílula branca que desliza), com abas. */}
      <div
        className="fin__segmented nv-segmented nv-segmented--claro"
        role="tablist"
        aria-label="Escopo"
        style={{ ['--seg-n' as string]: escopos.length, ['--seg-i' as string]: Math.max(0, escopos.indexOf(escopo)) }}
      >
        <span className="nv-segmented__pilula" aria-hidden="true" />
        {escopos.map((s) => (
          <button type="button" key={s} role="tab" aria-selected={escopo === s} className={escopo === s ? 'fin__seg nv-seg nv-seg--ativo' : 'fin__seg nv-seg'} onClick={() => trocar(s)}>
            {ROTULO[s]}
          </button>
        ))}
      </div>

      {progresso.total > 0 && (
        <div className={progresso.done ? 'bag__barra bag__barra--completa' : 'bag__barra'} role="progressbar" aria-valuemin={0} aria-valuemax={progresso.total} aria-valuenow={progresso.packed}>
          <span style={{ width: `${(progresso.packed / progresso.total) * 100}%` }} />
        </div>
      )}

      {!online && <p className="auth-note bag__offline">Sem conexão — marcar e adicionar continuam funcionando; sobe quando a rede voltar.</p>}
      {fase === 'depois' && <p className="auth-note">Viagem encerrada — a lista fica como lembrança.</p>}

      {escopo === 'trip' && itens.length > 0 && (
        <div className="chip-row">
          <button type="button" className={soMeus ? 'pick pick--on' : 'pick'} aria-pressed={soMeus} onClick={() => setSoMeus((v) => !v)}>
            Meus
          </button>
        </div>
      )}

      {carregando && !estado && <Skeleton height={56} count={4} />}
      {erro && !estado && <ErrorState title="Não foi possível carregar" description="Verifique a conexão e tente de novo." onRetry={() => void carregar(tripId)} />}

      {estado && progresso.done && (
        <div className="bag__pronto nv-pop" data-testid="bag-pronto">
          <Ilustracao nome="concluido" tamanho={52} />
          <span className="bag__pronto-texto">
            Mala pronta
            <small>{progresso.total === 1 ? '1 item · boa viagem' : `${progresso.total} itens · boa viagem`}</small>
          </span>
        </div>
      )}

      {estado && itens.length === 0 && (
        <EmptyState
          ilustracao="bagagem"
          title={escopo === 'private' ? 'Sua mala começa aqui' : escopo === 'family' ? 'Nada da família ainda' : 'Nada coletivo ainda'}
          description={escopo === 'trip' ? 'O que o grupo precisa levar? Quem leva pode ser escolhido depois.' : 'Toque em + para adicionar o primeiro item — ou use as Sugestões.'}
        />
      )}

      {grupos.map((g) => (
        <Card key={g.category} className="bag__grupo">
          <div className="bag__cat">
            <span>{BAGGAGE_CATEGORY_LABELS[g.category]}</span>
            <span className="mono">{g.packed}/{g.itens.length}</span>
          </div>
          {g.itens.map((i) => (
            <div
              key={i.id}
              className={`bag__item${i.packedAt ? ' bag__item--feito' : ''}`}
              data-testid="bag-item"
              onContextMenu={(e) => {
                e.preventDefault();
                if (i.canEdit) setEditando(i);
              }}
            >
              <button type="button" className="bag__marcar" aria-pressed={!!i.packedAt} aria-label={`${i.packedAt ? 'Desmarcar' : 'Marcar'} ${i.title}`} onClick={() => void marcar(i.id, !i.packedAt)}>
                <span className={`bag__cx${i.packedAt ? ' bag__cx--ok' : ''}`} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path d="m5 12.5 4.5 4.5L19 7.5" />
                  </svg>
                </span>
                <span className="bag__titulo">{i.title}</span>
              </button>
              {escopo === 'trip' && (
                i.assigneeMemberId ? (
                  <span className={`bag__quem${i.assigneeMemberId === meuMemberId ? ' bag__quem--eu' : ''}`}>{i.assigneeMemberId === meuMemberId ? 'Você' : nomeDe(i.assigneeMemberId)}</span>
                ) : (
                  <button type="button" className="bag__quem bag__quem--sem" onClick={() => void editar(i.id, { assigneeMemberId: meuMemberId })}>
                    Eu levo
                  </button>
                )
              )}
              {i.packedAt && i.packedByMemberId && i.packedByMemberId !== meuMemberId && escopo !== 'private' && (
                <span className="bag__por">por {nomeDe(i.packedByMemberId)}</span>
              )}
              {i.canEdit && (
                <button type="button" className="bag__editar" aria-label={`Editar ${i.title}`} onClick={() => setEditando(i)}>
                  <Icon name="editar" size={16} />
                </button>
              )}
            </div>
          ))}
        </Card>
      ))}

      {estado && progresso.packed > 0 && fase === 'durante' && (
        <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => void prepararVolta(escopo)}>
          Preparar a volta (desmarcar tudo)
        </button>
      )}

      {novoAberto && fase !== 'depois' && (
        <Sheet titleId="bag-novo-titulo" onFechar={() => setNovoAberto(false)} testId="bag-novo-sheet">
          <h2 className="sheet__title" id="bag-novo-titulo">Novo item</h2>
          <p className="sheet__text">Um por vez com Enter, ou cole uma lista inteira.</p>
          <form
            className="bag__novo"
            onSubmit={(e) => {
              e.preventDefault();
              void enviarTexto();
            }}
          >
            <div className="bag__input">
              <input
                ref={entrada}
                className="field__input"
                placeholder={escopo === 'trip' ? 'Item da viagem…' : 'Item…'}
                value={texto}
                aria-label="Adicionar item"
                enterKeyHint="done"
                onChange={(e) => setTexto(e.target.value)}
                onPaste={(e) => {
                  const colado = e.clipboardData.getData('text');
                  if (colado.includes('\n')) {
                    e.preventDefault();
                    const titulos = splitPastedItems(colado);
                    if (titulos.length) void adicionar({ scope: escopo, titulos, category: categoria });
                  }
                }}
              />
              <button type="submit" className="nv-btn nv-btn--primary" disabled={!texto.trim()} aria-label="Adicionar">
                <Icon name="criar" size={18} />
              </button>
            </div>
            <span className="nv-section-label">Categoria</span>
            <div className="chip-row chip-row--quebra bag__cats" role="radiogroup" aria-label="Categoria">
              {BAGGAGE_CATEGORIES.map((c) => (
                <button type="button" key={c} role="radio" aria-checked={categoria === c} className={categoria === c ? 'pick pick--on' : 'pick'} onClick={() => setCategoria(c)}>
                  {BAGGAGE_CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>
          </form>
          <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setNovoAberto(false)}>
            Pronto
          </button>
        </Sheet>
      )}

      {editando && (
        <EditarItem
          item={editando}
          escopo={escopo}
          membros={members.map((m) => ({ memberId: m.memberId, name: m.name }))}
          onFechar={() => setEditando(null)}
          onSalvar={async (patch) => {
            await editar(editando.id, patch);
            setEditando(null);
          }}
          onExcluir={async () => {
            await excluir(editando.id);
            setEditando(null);
          }}
        />
      )}
    </section>
  );
}

function EditarItem({
  item,
  escopo,
  membros,
  onFechar,
  onSalvar,
  onExcluir,
}: {
  item: ItemDaMala;
  escopo: BaggageScope;
  membros: { memberId: string; name: string }[];
  onFechar: () => void;
  onSalvar: (patch: { title?: string; category?: BaggageCategory; assigneeMemberId?: string | null }) => Promise<void>;
  onExcluir: () => Promise<void>;
}) {
  const { fotoDe } = usePessoas();
  const [titulo, setTitulo] = useState(item.title);
  const [categoria, setCategoria] = useState<BaggageCategory>(item.category);
  const [responsavel, setResponsavel] = useState<string | null>(item.assigneeMemberId);
  return (
    <Sheet titleId="bag-editar" onFechar={onFechar}>
        <h2 className="sheet__title" id="bag-editar">Editar item</h2>
        <label className="field">
          <span className="field__label">Nome</span>
          <input className="field__input" value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={120} />
        </label>
        <div className="chip-row" role="radiogroup" aria-label="Categoria">
          {BAGGAGE_CATEGORIES.map((c) => (
            <button type="button" key={c} role="radio" aria-checked={categoria === c} className={categoria === c ? 'pick pick--on' : 'pick'} onClick={() => setCategoria(c)}>
              {BAGGAGE_CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
        {escopo === 'trip' && (
          <>
            <p className="auth-note" style={{ marginBottom: 4 }}>Quem leva</p>
            <div className="chip-row">
              <button type="button" className={responsavel === null ? 'pick pick--on' : 'pick'} onClick={() => setResponsavel(null)}>
                Ninguém ainda
              </button>
              {membros.map((m) => (
                <button type="button" key={m.memberId} className={responsavel === m.memberId ? 'pick pick--on' : 'pick'} onClick={() => setResponsavel(m.memberId)}>
                  <FotoAvatar nome={m.name} url={fotoDe(m.memberId).url} cor={fotoDe(m.memberId).cor} tamanho={20} className="bag__mini-avatar" />
                  {m.name}
                </button>
              ))}
            </div>
          </>
        )}
        <div className="fin__ctas">
          <button type="button" className="nv-btn nv-btn--ghost-danger" onClick={() => void onExcluir()}>
            Excluir
          </button>
          <button
            type="button"
            className="nv-btn nv-btn--primary"
            disabled={!titulo.trim()}
            onClick={() =>
              void onSalvar({
                ...(titulo.trim() !== item.title ? { title: titulo.trim() } : {}),
                ...(categoria !== item.category ? { category: categoria } : {}),
                ...(escopo === 'trip' && responsavel !== item.assigneeMemberId ? { assigneeMemberId: responsavel } : {}),
              })
            }
          >
            Salvar
          </button>
        </div>
    </Sheet>
  );
}
